import { HHBox, HHButton } from '@hinge-health/react-component-library';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useState } from 'react';
import { tokenizeJson } from '../utils/json-tokenize.util';

export const JSON_VIEWER_TOGGLE_TEST_ID = 'json-viewer-toggle-button';
export const JSON_VIEWER_CONTENT_TEST_ID = 'json-viewer-content';

/*
 * Source of unique DOM ids for the aria-controls/id pairing. React 17 has no
 * `useId`, and the overview tab renders one viewer per assessment, so a
 * shared constant id would be duplicated across the document.
 */
let nextContentIdSuffix = 0;

export interface JsonViewerProps {
  value: unknown;
  label?: string;
  defaultExpanded?: boolean;
}

/**
 * Generic collapsible JSON tree viewer for JSONB payloads (validation
 * reports, assessment reports, raw metadata). Syntax colouring comes from
 * the lightweight lexer in `utils/json-tokenize.util` rather than a full
 * highlighting library.
 */
export const JsonViewer = ({
  value,
  label = 'View JSON',
  defaultExpanded = false,
}: JsonViewerProps): JSX.Element => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // `data-testid` stays a fixed constant so tests can query it; only the DOM
  // id has to be unique. The lazy initialiser runs once per instance.
  const [contentId] = useState(() => {
    nextContentIdSuffix += 1;

    return `${JSON_VIEWER_CONTENT_TEST_ID}-${nextContentIdSuffix}`;
  });
  const json = JSON.stringify(value, null, 2) ?? 'null';

  return (
    <HHBox>
      <HHButton
        hhVariant="variant-bypass"
        variant="text"
        size="small"
        data-testid={JSON_VIEWER_TOGGLE_TEST_ID}
        aria-expanded={expanded}
        aria-controls={expanded ? contentId : undefined}
        endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
        onClick={(): void => setExpanded(current => !current)}
      >
        {label}
      </HHButton>
      {expanded && (
        <HHBox
          component="pre"
          id={contentId}
          data-testid={JSON_VIEWER_CONTENT_TEST_ID}
          sx={{
            backgroundColor: 'grey.100',
            padding: 2,
            borderRadius: 1,
            overflowX: 'auto',
            fontSize: '0.75rem',
            margin: 0,
          }}
        >
          {tokenizeJson(json).map((token, index) => {
            const tokenKey = `json-token-${index}-${token.text.slice(0, 8)}`;
            return (
              <span key={tokenKey} style={{ color: token.color }}>
                {token.text}
              </span>
            );
          })}
        </HHBox>
      )}
    </HHBox>
  );
};
